<template>
  <div>
    <div class="flex px-3.5 md:px-4 py-6 items-start border-b-2 border-light-silver gap-2 md:gap-3">
      <div class="flex-shrink-0 w-12">
        <NuxtImg
          v-if="clinicList.clinic_type === 'nurse-station'"
          loading="lazy"
          src="/icons/nurse-icon.svg"
          class="w-9"
          alt="clinic icon"
          width="36"
          height="36"
        />

        <NuxtImg
          v-else
          loading="lazy"
          src="/icons/clinic-icon.svg"
          class="w-9"
          alt="clinic icon"
          width="36"
          height="36"
        />
      </div>

      <div class="flex flex-col flex-grow">
        <div class="flex justify-between items-start w-full">
          <div class="flex items-start" @click="getTests(clinicList)">
            <LazyIconsMarkerPin
              :color="
                clinicList.clinic_type === 'nurse-station' && isNurseStationServiceEnabled ? '#00a1aa' : '#931363'
              "
              class="mx-1 mt-1 flex-shrink-0"
              width="12.4"
              height="16.412"
            />
            <h3 class="cursor-pointer text-dark-blue font-bold leading-tight">
              {{ clinicList.title }}
            </h3>
          </div>
          <div class="text-blog-14-sm text-slate-grey whitespace-nowrap ml-2">
            {{ Number(clinicList.distance).toFixed(1) }} {{ $t("distance." + distanceUnit) }}
          </div>
        </div>

        <p class="text-blog-14-sm py-4 text-slate-grey leading-relaxed">
          {{ clinicList.content }}
        </p>

        <div class="flex items-center justify-between mt-auto">
          <BaseButton @buttonClicked="getTests(clinicList)" class="w-40 font-bold" :text="$t('common.btnSelect')" />
          <!-- A real link (not a button) so crawlers can follow it to the clinic page. -->
          <NuxtLink
            v-if="showClinicDetailBtn && clinicList.slug"
            :to="localePath(`/clinics/${clinicList.slug}`)"
            class="text-sm px-3 py-2 text-secondary underline font-semibold hover:no-underline"
          >
            {{ $t("city.clinicDetailBtn") }}
            <!-- Names the clinic in the link text for screen readers and crawlers ("Clinic details" alone repeats). -->
            <span class="sr-only">– {{ clinicList.title }}</span>
          </NuxtLink>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useMapStore } from "@/stores/map";
import { useCheckoutStore } from "~/stores/checkout";
import { useProductStore } from "~/stores/filter";
import { useGetStartedModalStore } from "~/stores/get-started-modal";
import { useGlobalStore } from "~/stores/global";
const router = useRouter();
const path = "/get-started";
const globalStore = useGlobalStore();
const mapStore = useMapStore();
const getStartedStore = useGetStartedModalStore();
const productStore = useProductStore();
const checkoutStore = useCheckoutStore();
const localePath = useLocalePath();
const locale = computed(() => globalStore.siteCurrentLocale);
const route = useRoute();

const distanceUnit = ref(globalStore.siteSettings.distance_unit.value);
const showClinicDetailBtn = ref(globalStore.siteSettings.show_clinic_details_page);
const isNurseStationServiceEnabled = toRef(globalStore.siteNurseStationServiceEnabled);

const props = defineProps({
  color: {
    type: String,
    default: "primary/10",
  },
  clinicList: {
    type: Object,
  },
});

const getTests = async (item) => {
  productStore.clearAllFilters();
  const pathWithoutLocale = route.path.replace(`/${locale.value}`, "").split("/")[1];

  switch (pathWithoutLocale) {
    case "get-started":
      await navigateTo({
        query: {
          ...route.query,
          slug: item.slug,
          ...(location.content ?? { content: location.content }),
        },
      });
      getStartedStore.navigate("show_tests");
      break;

    case "tests":
      const { data: clinicDetail } = await useFetchClinicDetailBySlug(item.slug);
      if (clinicDetail.value) {
        let clinicFormatedDetail = "";
        clinicFormatedDetail = {
          id: clinicDetail.value.data.id,
          title: clinicDetail.value.data.display_title,
          slug: clinicDetail.value.data.slug,
        };
        mapStore.setSelectedClinic(clinicFormatedDetail);
      }

      checkoutStore.removeTestPanelLists(productStore.selectedTest);

      return navigateTo(
        localeUrlQuery({
          query: {
            ...route.query,
            slug: item.slug,
          },
        })
      );
      break;
    case "clinics":
      mapStore.openPanel();
      navigateTo(
        localeUrlQuery({
          query: {
            ...route.query,
            slug: item.slug,
            ...(location.content ?? { content: location.content }),
          },
        })
      );
      break;
    default:
      checkoutStore.setSelectedClinicCookieData(item.id, productStore.selectedTest, "test-page");
      navigateTo(
        localeUrlQuery({
          path: "/checkout",
          query: {
            clinic_id: item.id,
            test_id: checkoutStore.getCheckoutSession.test_id,
            start_point: "test-page",
          },
        })
      );
      break;
  }
};
</script>
<style scoped>
.modal-header {
  box-shadow: 0px 3px 6px #00000029;
  background: var(--unnamed-color-ffffff) 0% 0% no-repeat padding-box;
  background: #ffffff 0% 0% no-repeat padding-box;
}
</style>
